// @flow
import React, { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { EventActions } from 'modules/event/actions';
import { EventSelector } from 'modules/event/selector';
import { isEmpty } from 'lodash';
import TicketMetaDescription from 'components/molecules/ticketMetaDescription';
import TicketSelectionBox from 'components/organisms/ticketSelectionBox';
import EventTicketGrid from 'components/molecules/eventTicketGrid';
import { CallToAction } from 'components/atoms/callToAction';
import EventTicketCart from 'components/organisms/ticketCart';
import { useViewport } from 'components/templates/viewportProvider';
//eslint-disable-next-line
import styles from "./styles.scss";

type Props = {
  event_details: Object,
  selectedTableDetails: Object,
  standingTickets: number,
  manageTableTickets: () => void,
};

const EventTicketSelection = (props: Props) => {
  const {
    event_details,
    selectedTableDetails,
    standingTickets,
    manageTableTickets,
    manageStandingTicketCount,
    onContactUsClick,
    placeholder,
    value,
    onSelect,
    extractDates,
    updateOverlay
  } = props;

  const {
    availableStandingTickets,
    availableTables,
    seating_ticket_price,
    standing_ticket_price,
    table_seating,
    title,
    event_time
  } = event_details;

  const { tableStrength, tableIds = [] } = selectedTableDetails;
  const [showCart, setShowCart] = useState(false);
  const [isTableSelected, setIsTableSelected] = useState(tableIds.length > 0);
  const { isMobile } = useViewport();

  useEffect(() => {
    // called when component is unmounted to clear all the redux data.
    return function cleanUp() {
      manageStandingTicketCount(0);
      manageTableTickets({ tableStrength: 0, tableIds: [] });
      iterateTableGrid(table_seating);
      setShowCart(false);
    };
  }, [updateOverlay]);

  const getTicketType = (eventDetails) => {
    const restaurantName = eventDetails.venue_name;
    if((restaurantName === 'Terrace') || (restaurantName === 'Windmills BIAL') || (restaurantName === 'Oota,Whitefield')) {
      return 'Entry';
    }
    return 'Standing';
  }

  let standing_tickets_data = {
    ticket_type: getTicketType(event_details),
    ticket_type_id: '1',
    available_tickets: availableStandingTickets,
    price: standing_ticket_price,
    selected: standingTickets,
    total_cost: (standing_ticket_price > 499) ? 1.18 * (standing_ticket_price * standingTickets) : standing_ticket_price * standingTickets,
  };

  let seating_tickets_data = {
    ticket_type: 'Sitting',
    ticket_type_id: '2',
    price: seating_ticket_price,
    selected: isTableSelected ? tableStrength : 0,
    total_cost: isTableSelected
      ? (seating_ticket_price > 499) ? 1.18 * (seating_ticket_price * tableStrength) : seating_ticket_price * tableStrength
      : 0,
    selectedTableIDs: isTableSelected ? tableIds : [],
  };

  let temp_cart_data = { standing_tickets_data, seating_tickets_data };

  const onTableCapacitySelect = tableCapacity => {
    //Used when user changes the table strength from dropdown.
    manageTableTickets({ tableStrength: tableCapacity, tableIds: [] });
    setIsTableSelected(false);
    iterateTableGrid(table_seating);
  };

  const resetTableCapacity = () => {
    manageStandingTicketCount(0);
    manageTableTickets({ tableStrength: 0, tableIds: [] });
    setIsTableSelected(false);
    iterateTableGrid(table_seating);
  };

  const showTicketDetails = () => (
    <>
      {standing_tickets_data.price > 0 && (
        <TicketSelectionBox
          data={standing_tickets_data}
          type={'standing'}
          className={'standing-box'}
          availability={availableStandingTickets}
        />
      )}
      {(!isEmpty(table_seating) && seating_tickets_data.price > 0) && (
        <TicketSelectionBox
          data={seating_tickets_data}
          type={'sitting'}
          className={'sitting-box'}
          onDropDownClick={capacity => onTableCapacitySelect(capacity)}
          availability={availableTables}
        />
      )}
      {!isMobile && (
        <div className={'desktop-goto-cart'}>{renderAddToCartInfo()}</div>
      )}
    </>
  );

  const renderAddToCartInfo = () => (
    <>
      {(tableIds.length > 0 || standingTickets > 0) && (
        <CallToAction
          text={'Next'}
          onClick={() => setShowCart(true)}
          className={'add-to-cart'}
        />
      )}
      <div className={'contact-info'}>
        <h3 className={'bulk-booking-text'}>
          For Group bookings with more than 8 Guests, please{' '}
        </h3>
        <a href={`#contact`} onClick={onContactUsClick}>
          <h3 className={'contact-us-text'}> contact us</h3>
        </a>
      </div>
      <br />
      <br />
      <br />
    </>
  );

  const iterateTableGrid = table_seating => {
    if (!isEmpty(table_seating)) {
      Object.values(table_seating).map(row_data => {
        row_data.map(table => {
          table.imageSelected = false;
        });
      });
    }
  };

  return (
    <Row
      className={`${
        isMobile ? 'mobile-container' : ''
      } ticket-selection-overlay`}
    >
      <Col sm={12} xs={12} md={6} lg={6} className={'left-section'}>
        <TicketMetaDescription
          data={event_details}
          onSelect={onSelect}
          placeholder={placeholder}
          value={value}
          showCart={showCart}
          extractDates={extractDates}
          resetTableCapacity={resetTableCapacity}
        />
        {!showCart && showTicketDetails()}
      </Col>
      {/*Used to show Ticket Table Layout based on boolean condition.*/}
      <Col sm={12} xs={12} md={6} lg={6} className={'right-section'}>
        {!showCart && !isEmpty(table_seating) && (
          <EventTicketGrid
            personCount={tableStrength}
            gridLayout={table_seating}
            tableSelected={tableId => {
              manageTableTickets({
                tableStrength: tableStrength,
                tableIds: tableId,
              });
              setIsTableSelected(tableId.length > 0);
            }}
            eventDate={event_details.event_time?.concert_date}
          />
        )}
        {/*Used to show ticket cart with all details based on boolean condition.*/}
        {showCart && (
          <EventTicketCart
            onEditClick={() => (setShowCart(false), resetTableCapacity())}
            data={temp_cart_data}
            eventTimeID={event_details.event_time?.id}
            onClosePress={onContactUsClick}
            eventTitle={event_details.title}
            eventDate={event_details.event_time?.concert_date}
          />
        )}
      </Col>
      {isMobile && !showCart && (
        <div className={'mobile-goto-cart'}>{renderAddToCartInfo()}</div>
      )}
    </Row>
  );
};

const mapStateToProps = state => {
  return {
    standingTickets: EventSelector.getStandingTickets(state),
    selectedTableDetails: EventSelector.getTableSelected(state),
  };
};

const mapDispatchToProps = dispatch => {
  const { manageTableTickets, manageStandingTicketCount } = EventActions;
  return bindActionCreators(
    {
      manageTableTickets,
      manageStandingTicketCount,
    },
    dispatch
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(EventTicketSelection);
